import { OptionModalProps } from ".";
import { useState } from "react";
import ListEditor, { ListEditTableDescription } from "./listEditor";
import { CoreModal, CorePaper, CoreTitleCard } from "../../../core";
import { useMobileLayout } from "../../../hooks";
import { ProctoringOption } from "@verity/proctoring-option/types";
import { Link, Typography } from "@mui/material";
import { Trans } from "react-i18next";
import i18nPackage from "../../../i18n/i18n";

export default function UrlAllowBlockLists<T extends Partial<ProctoringOption>>({
	onClose,
	currOption,
	updateOption,
}: OptionModalProps<T>) {
	const { tabWhiteList } = currOption;
	const [allowList, setAllowList] = useState(tabWhiteList ?? []);
	const isMobileLayout = useMobileLayout();

	const onSave = () => {
		const uniqueList = Array.from(new Set(allowList.map((url) => url.trim())));
		updateOption({
			...currOption,
			tabWhiteList: uniqueList,
		});
		onClose();
	};

	const content = (
		<ListEditor
			currList={allowList}
			updateList={setAllowList}
			placeholder={i18nPackage.t("editQuizPage.configurationStep.urlAllowBlockListsModal.placeholder")}
			emptyText={i18nPackage.t("editQuizPage.configurationStep.urlAllowBlockListsModal.emptyText")}
		/>
	);

	const ariaLabel = i18nPackage.t("editQuizPage.configurationStep.urlAllowBlockListsModal.learnMoreAriaLabel");
	return (
		<CoreModal
			open
			onClose={onClose}
			title={isMobileLayout ? undefined : i18nPackage.t("editQuizPage.configurationStep.urlAllowBlockListsModal.title")}
			minWidth={isMobileLayout ? undefined : "min(65%, 700px)"}
			minHeight={isMobileLayout ? undefined : "min(80%, 500px)"}
			widthFixed
			heightFixed={true}
			fullScreen={isMobileLayout}
			showTopCloseButton={!isMobileLayout}
			dialogContentBackground={isMobileLayout ? "#f4f4f4" : undefined}
			buttons={[
				{
					text: i18nPackage.t("editQuizPage.configurationStep.urlAllowBlockListsModal.cancel"),
					color: "secondary",
					style: { marginLeft: isMobileLayout ? "auto" : undefined },
					onClick: onClose,
				},
				{
					text: i18nPackage.t("editQuizPage.configurationStep.urlAllowBlockListsModal.save"),
					color: "primary",
					onClick: onSave,
				},
			]}
		>
			{isMobileLayout ? (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						width: "100%",
						gap: "10px",
						background: "f4f4f4",
					}}
				>
					<CoreTitleCard
						title={i18nPackage.t("editQuizPage.configurationStep.urlAllowBlockListsModal.title")}
						description={
							<Typography component="span">
								<Trans
									i18nKey="editQuizPage.configurationStep.urlAllowBlockListsModal.description"
									components={{
										a: (
											<Link
												href="https://support.yuja.com/hc/en-us/articles/18378524033559-Overview-of-YuJa-Verity-Proctor-Settings"
												target="_blank"
												rel="noreferrer"
												aria-label={ariaLabel}
											/>
										),
									}}
								/>
							</Typography>
						}
						style={{ minWidth: "300px", padding: "15px 10px 10px 10px", marginTop: "10px" }}
						slotProps={{ title: { style: { color: "#42296E" } }, text: { style: { gap: "5px" } } }}
					/>
					<CorePaper
						component="section"
						sx={{
							display: "flex",
							flexWrap: "wrap",
							justifyContent: "space-between",
							width: "100%",
							columnGap: "24px",
							padding: "10px",
							boxSizing: "border-box",
						}}
					>
						{content}
					</CorePaper>
				</div>
			) : (
				<>
					<ListEditTableDescription>
						<Typography component="span">
							<Trans
								i18nKey="editQuizPage.configurationStep.urlAllowBlockListsModal.description"
								components={{
									a: (
										<Link
											href="https://support.yuja.com/hc/en-us/articles/18378524033559-Overview-of-YuJa-Verity-Proctor-Settings"
											target="_blank"
											rel="noreferrer"
											aria-label={ariaLabel}
										/>
									),
								}}
							/>
						</Typography>
					</ListEditTableDescription>
					{content}
				</>
			)}
		</CoreModal>
	);
}
